import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Grid,
  TextField,
  Paper,
  Typography,
  Box,
  Dialog,
  DialogContent,
  DialogActions,
  DialogContentText,
  TableContainer,
  Tooltip,
  Autocomplete,
  CircularProgress,
  IconButton,
} from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import VisibilityIcon from "@mui/icons-material/Visibility";
import InputAdornment from "@mui/material/InputAdornment";
import Cookie from "js-cookie";
import { useMaterialUIController } from "context";
import {
  ViewButton,
  DeleteButton,
  StyledTypography,
  StyledButton,
  StyledButtonC,
  StyledAlert,
  StyledSnackbar,
  inputStyle,
  StyledAgGridContainer,
  StyledTableContainer,
} from "components/StyledComponents/styledComponents.js";
import { employeeStyles } from "components/Styles/employeeMasterStyles";
import { subDialogContentStyle, dialogContentTextStyle } from "components/Styles/approvalStyles";
import ResponsiveTabs from "components/StyledComponents/styledComponents";
import URLs from "constants/urls";

export default function Applicant() {
  const [activeTab, setActiveTab] = useState(0);
  const [isEditMode, setIsEditMode] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [alertType, setAlertType] = useState("success");
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(0);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    dob: "",
    address: {
      doorNo: "",
      streetName: "",
      cityName: "",
      districtName: "",
      state: "",
      zipCode: "",
    },
    phone: "",
    emailId: "",
    alternativePhone: "",
    reference: "",
    role: null,
    ctcPerYear: "",
    basic: "",
    dateOfJoining: "",
    resume: null,
    additionalComments: "",
  });
  const [roleData, setRoleData] = useState([]);
  const [hrms_token] = useState(Cookie.get("hrms_token"));
  const [loading, setLoading] = useState(false);
  const [controller] = useMaterialUIController();
  const { user } = controller;
  const [formErrors, setFormErrors] = useState({});
  const [phonePattern] = useState(/^[1-9]\d{9}$/);
  const [emailPattern] = useState(/^[^\s@]+@[^\s@]+\.[^\s@]+$/);
  const [openResumeDialog, setOpenResumeDialog] = useState(false);
  const [selectedResume, setSelectedResume] = useState(null);

  const MInput = inputStyle();

  useEffect(() => {
    fetchRoleData();
    fetchApplicants();
  }, []);

  const fetchRoleData = async () => {
    setLoading(true);
    const url = `${URLs.hrmsGetRoleDetails}`;
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${hrms_token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const data = await response.json();
      setRoleData(data);
    } catch (error) {
      console.error("Error fetching role data:", error);
      setMessage("Failed to fetch role data");
      setAlertType("error");
      setOpen(true);
    }
    setLoading(false);
  };

  const fetchApplicants = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${URLs.applicantsByIdd}/${user.companyId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${hrms_token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch applicants");
      }

      const data = await response.json();
      if (!Array.isArray(data)) {
        console.error("Received data is not an array:", data);
        throw new Error("Invalid data format received");
      }

      // Fetch role details for each applicant
      const applicantsWithRoles = await Promise.all(
        data.map(async (applicant) => {
          if (applicant.roleId) {
            try {
              const roleResponse = await fetch(`${URLs.hrmsGetRoleDetails}/${applicant.roleId}`, {
                method: "GET",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${hrms_token}`,
                },
              });
              if (roleResponse.ok) {
                const roleData = await roleResponse.json();
                return { ...applicant, role: roleData };
              }
            } catch (error) {
              console.error("Error fetching role:", error);
            }
          }
          return applicant;
        })
      );
      setTableData(applicantsWithRoles);
    } catch (error) {
      console.error("Error fetching applicants:", error);
      setMessage("Failed to fetch applicants");
      setAlertType("error");
      setOpen(true);
    }
    setLoading(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      if (name === "ctcPerYear") {
        const ctcValue = value.replace(/,/g, "");
        const basicValue = (parseFloat(ctcValue) * 0.5).toFixed(2);
        setFormData((prev) => ({
          ...prev,
          [name]: value,
          basic: basicValue.toString(),
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          [name]: value,
        }));
      }
    }
  };

  const handleRoleChange = (event, newValue) => {
    setFormData((prev) => ({
      ...prev,
      role: newValue,
    }));
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.firstName.trim()) errors.firstName = "First Name is required";
    if (!formData.lastName.trim()) errors.lastName = "Last Name is required";
    if (!formData.dob) errors.dob = "Date of Birth is required";
    if (!formData.role) errors.role = "Role is required";
    if (!formData.phone) errors.phone = "Phone Number is required";
    if (!formData.emailId) errors.emailId = "Email is required";
    if (!formData.dateOfJoining) errors.dateOfJoining = "Date of Joining is required";
    if (!formData.ctcPerYear) errors.ctcPerYear = "CTC is required";
    if (!formData.basic) errors.basic = "Basic is required";

    if (!isEditMode && !formData.resume) errors.resume = "Resume is required";

    if (formData.phone && !phonePattern.test(formData.phone)) {
      errors.phone = "Enter valid 10-digit phone number";
    }
    if (formData.alternativePhone && !phonePattern.test(formData.alternativePhone)) {
      errors.alternativePhone = "Enter valid 10-digit phone number";
    }
    if (formData.emailId && !emailPattern.test(formData.emailId)) {
      errors.emailId = "Enter valid email address";
    }
    if (formData.address.zipCode && !/^\d{6}$/.test(formData.address.zipCode)) {
      errors.zipCode = "Enter valid 6-digit zip code";
    }

    const ctc = parseFloat(formData.ctcPerYear?.toString().replace(/,/g, "")) || 0;
    const basic = parseFloat(formData.basic?.toString().replace(/,/g, "")) || 0;
    if (basic > ctc) {
      errors.basic = "Basic cannot be greater than CTC";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setMessage("Please fix the form errors");
      setAlertType("error");
      setOpen(true);
      return;
    }

    if (!formData.role) {
      setMessage("Please select a role");
      setAlertType("error");
      setOpen(true);
      return;
    }

    const payload = {
      applicantId: isEditMode ? formData.applicantId : 0,
      companyId: user.companyId,
      firstName: formData.firstName,
      lastName: formData.lastName,
      dateOfJoining: formData.dateOfJoining,
      dateOfBirth: formData.dob,
      doorNo: formData.address.doorNo,
      empAddress: {
        streetName: formData.address.streetName,
        cityName: formData.address.cityName,
        districtName: formData.address.districtName,
        stateName: formData.address.state,
        zipCode: formData.address.zipCode,
      },
      phoneNumber: formData.phone,
      alternatePhoneNumber: formData.alternativePhone,
      emailId: formData.emailId,
      reference: formData.reference,
      role: {
        roleId: formData.role.roleId,
        roleName: formData.role.roleName,
      },
      ctcPerYear: parseFloat(formData.ctcPerYear?.toString().replace(/,/g, "")) || 0,
      basic: parseFloat(formData.basic?.toString().replace(/,/g, "")) || 0,
      additionalComments: formData.additionalComments,
      roleId: formData.role.roleId,
    };

    if (!isEditMode || (isEditMode && formData.resume)) {
      payload.resumeAttachment = formData.resume ? await convertFileToBase64(formData.resume) : "";
    }

    try {
      const url = isEditMode
        ? `${URLs.hrmsUpdateApplicant}/${formData.applicantId}`
        : URLs.hrmsPostApplicant;

      const response = await fetch(url, {
        method: isEditMode ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${hrms_token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message ||
            (isEditMode ? "Failed to update applicant" : "Failed to create applicant")
        );
      }

      setMessage(isEditMode ? "Applicant updated successfully" : "Applicant created successfully");
      setAlertType("success");
      setOpen(true);
      handleCancel();
      fetchApplicants();
    } catch (error) {
      console.error("Error:", error);
      setMessage(error.message);
      setAlertType("error");
      setOpen(true);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(`${URLs.hrmsDeleteApplicant}/${deleteId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${hrms_token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to delete applicant");
      }

      setMessage("Applicant deleted successfully");
      setAlertType("success");
      setOpen(true);
      setOpenDialog(false);
      fetchApplicants();
    } catch (error) {
      console.error("Error:", error);
      setMessage(error.message);
      setAlertType("error");
      setOpen(true);
    }
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const tabs = [{ label: "Applicant List" }, { label: "Applicant Registration" }];

  const handleCancel = () => {
    setFormData({
      firstName: "",
      lastName: "",
      dob: "",
      address: {
        doorNo: "",
        streetName: "",
        cityName: "",
        districtName: "",
        state: "",
        zipCode: "",
      },
      phone: "",
      emailId: "",
      alternativePhone: "",
      reference: "",
      role: null,
      ctcPerYear: "",
      basic: "",
      dateOfJoining: "",
      resume: null,
      additionalComments: "",
    });
    setIsEditMode(false);
    setActiveTab(0);
  };

  const ActionCellRenderer = (props) => {
    const rowIndex = props.node.rowIndex;
    const rowData = props.api.getDisplayedRowAtIndex(rowIndex).data;

    const handleEditClick = () => {
      const formatDate = (dateString) => {
        if (!dateString) return "";
        try {
          const date = new Date(dateString);
          return date instanceof Date && !isNaN(date) ? date.toISOString().split("T")[0] : "";
        } catch (e) {
          console.error("Error formatting date:", e);
          return "";
        }
      };

      const applicantData = {
        ...rowData,
        dob: formatDate(rowData.dateOfBirth),
        dateOfJoining: formatDate(rowData.dateOfJoining),
        address: {
          doorNo: rowData.doorNo || "",
          streetName: rowData.empAddress?.streetName || "",
          cityName: rowData.empAddress?.cityName || "",
          districtName: rowData.empAddress?.districtName || "",
          state: rowData.empAddress?.stateName || "",
          zipCode: rowData.empAddress?.zipCode || "",
        },
        phone: rowData.phoneNumber || "",
        alternativePhone: rowData.alternatePhoneNumber || "",
        role: rowData.role || null,
        resume: null,
        ctcPerYear: rowData.ctcPerYear?.toString() || "",
        basic: rowData.basic?.toString() || "",
      };

      console.log("Edit data:", applicantData);
      setFormData(applicantData);
      setIsEditMode(true);
      setActiveTab(1);
    };

    const handleDelete = () => {
      setDeleteId(rowData.applicantId);
      setOpenDialog(true);
    };

    return (
      <div>
        <ViewButton type="button" onClick={handleEditClick}>
          <EditIcon />
        </ViewButton>
        <DeleteButton type="button" onClick={handleDelete}>
          <DeleteIcon />
        </DeleteButton>
      </div>
    );
  };
  const handleReset = () => {
    setFormData({
      firstName: "",
      lastName: "",
      dob: "",
      address: {
        doorNo: "",
        streetName: "",
        cityName: "",
        districtName: "",
        state: "",
        zipCode: "",
      },
      phone: "",
      emailId: "",
      alternativePhone: "",
      reference: "",
      role: null,
      ctcPerYear: "",
      basic: "",
      dateOfJoining: "",
      resume: null,
      additionalComments: "",
    });
    setIsEditMode(false);
  };

  ActionCellRenderer.propTypes = {
    api: PropTypes.shape({
      getDisplayedRowAtIndex: PropTypes.func.isRequired,
    }).isRequired,
    node: PropTypes.shape({
      rowIndex: PropTypes.number.isRequired,
    }).isRequired,
  };

  const ResumeViewer = ({ base64Data }) => {
    if (!base64Data) return null;

    const pdfUrl = base64Data.startsWith("data:")
      ? base64Data
      : `data:application/pdf;base64,${base64Data}`;

    return (
      <object data={pdfUrl} type="application/pdf" style={{ width: "100%", height: "80vh" }}>
        <p>Unable to display PDF. Please download to view.</p>
      </object>
    );
  };

  ResumeViewer.propTypes = {
    base64Data: PropTypes.string,
  };

  const handleResumeView = (resume) => {
    setSelectedResume(resume);
    setOpenResumeDialog(true);
  };

  const handleCloseResumeDialog = () => {
    setOpenResumeDialog(false);
    setSelectedResume(null);
  };

  const columnDefs = [
    {
      headerName: "Applicant Name",
      field: "applicantName",
      valueGetter: (params) => {
        return `${params.data.firstName || ""} ${params.data.lastName || ""}`.trim();
      },
      sortable: true,
      filter: true,
      flex: 2,
    },
    {
      headerName: "Email Id",
      field: "emailId",
      sortable: true,
      filter: true,
      flex: 1,
    },
    {
      headerName: "Phone Number",
      field: "phoneNumber",
      sortable: true,
      filter: true,
      flex: 1,
    },
    {
      headerName: "Role",
      field: "role",
      valueGetter: (params) => {
        const role = params.data.role;
        return role ? role.roleName : "";
      },
      sortable: true,
      filter: true,
      flex: 1,
    },
    {
      headerName: "CTC Per Annum",
      field: "ctcPerYear",
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueGetter: (params) => {
        const ctc = params.data.ctcPerYear || 0;
        return `₹${Number(ctc).toLocaleString("en-IN")}`;
      },
      sortable: true,
      filter: true,
      flex: 1,
    },
    {
      headerName: "Basic",
      field: "basic",
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueGetter: (params) => {
        const basic = params.data.basic || 0;
        return `₹${Number(basic).toLocaleString("en-IN")}`;
      },
      sortable: true,
      filter: true,
      flex: 1,
    },
    {
      headerName: "Resume",
      field: "resumeAttachment",
      cellRenderer: (params) => {
        if (!params.value) return null;
        return (
          <Tooltip title="View Resume">
            <IconButton
              size="small"
              onClick={() => handleResumeView(params.value)}
              sx={{ color: "#1976d2" }}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
        );
      },
      width: 100,
    },
    {
      headerName: "Actions",
      field: "actions",
      cellRenderer: ActionCellRenderer,
      width: 100,
    },
  ];

  return (
    <DashboardLayout>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          borderRadius: "12px",
          flexGrow: 1,
          boxShadow: "none",
          border: "none",
          height: "100%",
          backgroundColor: "transparent",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Dialog open={openDialog}>
          <DialogContent sx={subDialogContentStyle}>
            <DialogContentText sx={dialogContentTextStyle}>
              Are you sure you want to delete this applicant?
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={employeeStyles.dialogActions}>
            <StyledButton onClick={handleDelete}>Yes</StyledButton>
            <StyledButton onClick={() => setOpenDialog(false)} variant="outlined">
              No
            </StyledButton>
          </DialogActions>
        </Dialog>
        <StyledSnackbar
          open={open}
          autoHideDuration={5000}
          onClose={handleClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          severity={alertType}
        >
          <StyledAlert onClose={handleClose} severity={alertType}>
            {message}
          </StyledAlert>
        </StyledSnackbar>
        <ResponsiveTabs
          tabs={tabs}
          activeTab={activeTab}
          onChange={handleChange}
          labelColor="secondary"
          bgColor="#e0f7fa"
          sx={{
            width: "100%",
            "& .MuiTabs-flexContainer": {
              justifyContent: "flex-start",
              width: "100%",
            },
            "& .MuiTab-root": {
              minWidth: "250px",
              fontSize: "14px",
              fontWeight: "bold",
            },
          }}
        />
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            mt: 1,
            backgroundColor: "transparent",
            boxShadow: "none",
            border: "none",
            width: "100%",
            height: "calc(100% - 48px)",
            overflow: "hidden",
          }}
        >
          {activeTab === 0 && (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                backgroundColor: "#ffffff",
                borderRadius: "15px",
              }}
            >
              <StyledAgGridContainer
                component={Paper}
                className={`ag-theme-alpine ${employeeStyles.agTable}`}
                sx={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "15px",
                }}
              >
                <AgGridReact
                  rowData={tableData}
                  columnDefs={columnDefs}
                  pagination={true}
                  paginationPageSize={11}
                />
              </StyledAgGridContainer>
            </Box>
          )}
          {activeTab === 1 && (
            <StyledTableContainer
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                alignItems: "center",
                backgroundColor: "#ffffff",
                width: "100%",
                height: "100%",
                borderRadius: "15px",
                p: 2,
                overflow: "hidden",
              }}
            >
              <Grid
                container
                spacing={1}
                sx={{ width: "100%", m: 0, height: "100%", overflow: "hidden" }}
              >
                <Grid item xs={12} sx={{ width: "100%", height: "100%" }}>
                  <TableContainer
                    component={Paper}
                    sx={{
                      boxShadow: "none",
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                      overflow: "hidden",
                    }}
                  >
                    <div style={{ position: "relative", width: "100%" }}>
                      {isEditMode && (
                        <Tooltip title="Reset">
                          <StyledButtonC onClick={handleReset}>
                            <RestartAltIcon />
                          </StyledButtonC>
                        </Tooltip>
                      )}
                    </div>
                    <Grid
                      sx={{
                        ...employeeStyles.root,
                        p: 2,
                        height: "100%",
                        overflow: "hidden",
                      }}
                    >
                      <form onSubmit={handleSubmit} autoComplete="off" style={{ height: "100%" }}>
                        <Grid container spacing={2} sx={{ p: 2, height: "100%" }}>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              label="First Name"
                              name="firstName"
                              value={formData.firstName}
                              onChange={handleInputChange}
                              required
                              error={!!formErrors.firstName}
                              helperText={formErrors.firstName}
                              className={MInput.root}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              label="Last Name"
                              name="lastName"
                              value={formData.lastName}
                              onChange={handleInputChange}
                              required
                              error={!!formErrors.lastName}
                              helperText={formErrors.lastName}
                              className={MInput.root}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              type="date"
                              label="Date of Birth"
                              name="dob"
                              value={formData.dob}
                              onChange={handleInputChange}
                              required
                              error={!!formErrors.dob}
                              helperText={formErrors.dob}
                              InputLabelProps={{ shrink: true }}
                              className={MInput.root}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Autocomplete
                              options={roleData}
                              getOptionLabel={(option) => option.roleName || ""}
                              value={formData.role}
                              onChange={handleRoleChange}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Role"
                                  required
                                  error={!!formErrors.role}
                                  helperText={formErrors.role}
                                  className={MInput.root}
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              label="Phone"
                              name="phone"
                              value={formData.phone}
                              onChange={handleInputChange}
                              required
                              error={!!formErrors.phone}
                              helperText={formErrors.phone}
                              inputProps={{
                                maxLength: 10,
                                pattern: "[1-9]{1}[0-9]{9}",
                              }}
                              className={MInput.root}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              label="Email ID"
                              name="emailId"
                              type="email"
                              value={formData.emailId}
                              onChange={handleInputChange}
                              required
                              error={!!formErrors.emailId}
                              helperText={formErrors.emailId}
                              className={MInput.root}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              label="Alternative Phone"
                              name="alternativePhone"
                              value={formData.alternativePhone}
                              onChange={handleInputChange}
                              error={!!formErrors.alternativePhone}
                              helperText={formErrors.alternativePhone}
                              inputProps={{
                                maxLength: 10,
                                pattern: "[1-9]{1}[0-9]{9}",
                              }}
                              className={MInput.root}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              label="Door No"
                              name="address.doorNo"
                              value={formData.address.doorNo}
                              onChange={handleInputChange}
                              className={MInput.root}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              label="Street Name"
                              name="address.streetName"
                              value={formData.address.streetName}
                              onChange={handleInputChange}
                              className={MInput.root}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              label="City"
                              name="address.cityName"
                              value={formData.address.cityName}
                              onChange={handleInputChange}
                              className={MInput.root}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              label="District"
                              name="address.districtName"
                              value={formData.address.districtName}
                              onChange={handleInputChange}
                              className={MInput.root}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              label="State"
                              name="address.state"
                              value={formData.address.state}
                              onChange={handleInputChange}
                              className={MInput.root}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              label="Zip Code"
                              name="address.zipCode"
                              value={formData.address.zipCode}
                              onChange={handleInputChange}
                              error={!!formErrors.zipCode}
                              helperText={formErrors.zipCode}
                              inputProps={{
                                maxLength: 6,
                                pattern: "\\d{6}",
                              }}
                              className={MInput.root}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              label="Reference"
                              name="reference"
                              value={formData.reference}
                              onChange={handleInputChange}
                              className={MInput.root}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              label="CTC per Year"
                              name="ctcPerYear"
                              value={formData.ctcPerYear}
                              onChange={handleInputChange}
                              required
                              error={!!formErrors.ctcPerYear}
                              helperText={formErrors.ctcPerYear}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                              }}
                              className={MInput.root}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              label="Basic"
                              name="basic"
                              value={formData.basic}
                              onChange={handleInputChange}
                              required
                              error={!!formErrors.basic}
                              helperText={formErrors.basic}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                              }}
                              className={MInput.root}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              type="date"
                              label="Date of Joining"
                              name="dateOfJoining"
                              value={formData.dateOfJoining}
                              onChange={handleInputChange}
                              required
                              error={!!formErrors.dateOfJoining}
                              helperText={formErrors.dateOfJoining}
                              InputLabelProps={{ shrink: true }}
                              className={MInput.root}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              type="file"
                              label="Resume"
                              name="resume"
                              fullWidth
                              required
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  if (file.size > 5 * 1024 * 1024) {
                                    setMessage("File size should be less than 5MB");
                                    setAlertType("error");
                                    setOpen(true);
                                    return;
                                  }
                                  setFormData((prev) => ({
                                    ...prev,
                                    resume: file,
                                  }));
                                }
                              }}
                              className={MInput.root}
                              InputLabelProps={{ shrink: true }}
                              inputProps={{
                                accept: ".pdf,.doc,.docx",
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={8}>
                            <TextField
                              fullWidth
                              label="Additional Comments"
                              name="additionalComments"
                              multiline
                              rows={2}
                              value={formData.additionalComments}
                              onChange={handleInputChange}
                              className={MInput.root}
                            />
                          </Grid>
                          <Grid
                            item
                            xs={12}
                            sm={4}
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              justifyContent: "flex-end",
                            }}
                          >
                            <Box sx={{ display: "flex", gap: 2 }}>
                              {isEditMode && (
                                <StyledButton onClick={handleCancel} variant="outlined">
                                  Cancel
                                </StyledButton>
                              )}
                              <StyledButton type="submit" disabled={loading}>
                                {loading ? (
                                  <CircularProgress size={24} color="inherit" />
                                ) : isEditMode ? (
                                  "Update"
                                ) : (
                                  "Submit"
                                )}
                              </StyledButton>
                            </Box>
                          </Grid>
                        </Grid>
                      </form>
                    </Grid>
                  </TableContainer>
                </Grid>
              </Grid>
            </StyledTableContainer>
          )}
        </Box>
      </Box>
      <Dialog open={openResumeDialog} onClose={handleCloseResumeDialog} maxWidth="md" fullWidth>
        <DialogContent>
          <ResumeViewer base64Data={selectedResume} />
        </DialogContent>
        <DialogActions>
          <StyledButton onClick={handleCloseResumeDialog}>Close</StyledButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
